/**
 * R3.2.3 — Defaults création commerce depuis lignes taxonomie API (OR merge).
 * catalogOrientation / profileKey = identité soft uniquement (C10) — jamais gates.
 */
import { normalizeCategorieId } from '../config/commerce-categories.js';

const baseDefaults = () => ({
  moduleProduits: false,
  modulePrestations: false,
  moduleCaisse: false,
  devise: 'EUR',
  priseRDV: false,
  priseCreneaux: false,
  moduleCreneaux: false,
  reservationTable: false,
  livraison: false,
  clickAndCollect: false,
  paiementEnLigne: true,
  paiementSurPlace: true,
  pasGrilleMinutes: 15,
  dureesAutoriseesMinutes: [60, 90, 120],
});

/** Map colonnes API métier → objet defaults création commerçant. */
export function metierRowToDefaults(metier) {
  if (!metier) return baseDefaults();
  return {
    moduleProduits: Boolean(metier.moduleProduits),
    modulePrestations: Boolean(metier.modulePrestations),
    moduleCaisse: Boolean(metier.moduleCaisse),
    moduleCreneaux: Boolean(metier.moduleCreneaux),
    priseRDV: Boolean(metier.priseRDV),
    priseCreneaux: Boolean(metier.priseCreneaux),
    reservationTable: Boolean(metier.reservationTable),
    livraison: Boolean(metier.livraison),
    clickAndCollect: Boolean(metier.clickAndCollect),
    paiementEnLigne: metier.paiementEnLigne !== false,
    paiementSurPlace: metier.paiementSurPlace !== false,
    pasGrilleMinutes: metier.pasGrilleMinutes ?? 15,
    dureesAutoriseesMinutes: Array.isArray(metier.dureesAutoriseesMinutes)
      ? [...metier.dureesAutoriseesMinutes]
      : [60, 90, 120],
    devise: metier.devise || 'EUR',
  };
}

function resolveMetierDefaults(metier) {
  if (!metier) return null;
  if (metier.defaults && typeof metier.defaults === 'object') {
    return { ...metierRowToDefaults(metier), ...metier.defaults };
  }
  return metierRowToDefaults(metier);
}

/** Union OR des defaults pour plusieurs métiers (comme getDefaultsForCategories). */
export function getDefaultsFromMetiers(metiers, categorieIds) {
  const list = Array.isArray(categorieIds)
    ? categorieIds.map(normalizeCategorieId).filter(Boolean)
    : [];
  const merged = baseDefaults();
  if (list.length === 0) return merged;

  const byId = new Map(
    (metiers || []).map((m) => [normalizeCategorieId(m.id), m]),
  );
  const durees = new Set();

  for (const id of list) {
    const d = resolveMetierDefaults(byId.get(id));
    if (!d) continue;
    for (const [key, value] of Object.entries(d)) {
      if (typeof value === 'boolean') {
        if (value) merged[key] = true;
      } else if (Array.isArray(value)) {
        value.forEach((v) => durees.add(v));
      } else if (key === 'pasGrilleMinutes' && value) {
        merged.pasGrilleMinutes = Math.min(merged.pasGrilleMinutes || value, value);
      } else if (key === 'devise' && value) {
        merged.devise = value;
      }
    }
  }
  if (durees.size > 0) {
    merged.dureesAutoriseesMinutes = [...durees].sort((a, b) => a - b);
  }
  return merged;
}

/**
 * C10 — capabilities inchangées si catalogOrientation/profileKey mutés.
 * @throws {Error} si une capability diffère
 */
export function assertDefaultsIgnoreSoftIdentity(metiers, ids) {
  const idList = (ids || []).map(normalizeCategorieId).filter(Boolean);
  const before = getDefaultsFromMetiers(metiers, idList);
  const mutated = (metiers || []).map((m) => {
    if (!idList.includes(normalizeCategorieId(m.id))) return m;
    return {
      ...m,
      catalogOrientation: m.catalogOrientation === 'PRODUITS' ? 'PRESTATIONS' : 'PRODUITS',
      profileKey: `${m.profileKey || 'X'}_MUTATED`,
    };
  });
  const after = getDefaultsFromMetiers(mutated, idList);
  for (const key of Object.keys(before)) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      throw new Error(`C10: capability "${key}" modifiée par mutation soft identity`);
    }
  }
  return true;
}
